import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Scanner Audit — the daily N22000 AP Scanner Audit .xlsx, stored in FULL DETAIL
// (scan_events) so every scan line is queryable. Unlike Shift Approvals' uploader
// (which keeps a privacy-minimised one-row-per-driver summary), this retains address
// + signatory, which is what disputes / downtime / sequencing need.
//
// Views:
//  • Upload        — drop each day's .xlsx (idempotent re-drop)
//  • Case Lookup   — article → the scans around each day's delivery (every day it was on a
//                    run) + self-signature flag; push some or all of those days to the driver
//  • Address Search— address text → every scan there, any day, with a jump into Case Lookup
//  • Driver Day    — one driver's ordered day with downtime gaps
//  • Downtime      — every driver's biggest idle gap on a day
//  • Route Sequence— a driver's actual delivery run order (Delivered stream), exportable
const API = '/.netlify/functions/scanner-audit-full-sb';

const post = async (body) => {
  const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const text = await res.text();
  let j;
  try { j = JSON.parse(text); } catch {
    // Netlify's gateway answers with an HTML error page when the function exceeds its
    // 10s response window — the function itself keeps running. Flag it so the Upload
    // tab can poll for the background import instead of surfacing a JSON parse error.
    const err = new Error(`The server took too long to reply (HTTP ${res.status}).`);
    err.gatewayTimeout = true;
    throw err;
  }
  if (!j.success) throw new Error(j.error || 'Request failed');
  return j;
};

const cell = { padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.85rem', textAlign: 'left', whiteSpace: 'nowrap' };
const th = { ...cell, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.04em' };
const artCell = { ...cell, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem', color: 'var(--hp-dim)' };
const field = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.9rem' };
const stat = { flex: 1, minWidth: 110, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' };
const btn = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontSize: '0.9rem', cursor: 'pointer' };
const btnPrimary = { ...btn, background: 'var(--hp-accent, #2563eb)', color: '#fff', border: 'none' };

const AREA_NAME = { DARL: 'Darlinghurst', CITY: 'City', BWAY: 'Broadway' };

const TABS = [
  { key: 'upload', label: 'Upload' },
  { key: 'case', label: 'Case Lookup' },
  { key: 'address', label: 'Address Search' },
  { key: 'driver', label: 'Driver Day' },
  { key: 'downtime', label: 'Downtime' },
  { key: 'sequence', label: 'Route Sequence' },
];

function StatCard({ label, value, title }) {
  return (
    <div style={stat} title={title}>
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export default function ScannerAuditPage() {
  const [params] = useSearchParams();
  const incomingArticle = params.get('article') || '';   // deep-link from Customer Cases
  const incomingCase = params.get('case') || '';
  // Who's looking: the office sees every driver and the Upload tab; a granted team
  // leader sees only their own area's drivers and no uploader. Until whoami answers we
  // assume the restricted view, so nothing flashes up that this person may not have.
  const [scope, setScope] = useState(null);
  const isAdmin = !!(scope && scope.isAdmin);
  const scopedTo = scope && scope.areas ? scope.areas : null;
  const [tab, setTab] = useState(incomingArticle ? 'case' : 'upload');
  const [caseArticle, setCaseArticle] = useState(incomingArticle);   // Case Lookup's article (URL or Address Search jump)
  const [dates, setDates] = useState(null);        // loaded-dates strip
  const [retentionDays, setRetentionDays] = useState(365);
  const [drivers, setDrivers] = useState([]);

  const loadDates = useCallback(() => {
    post({ action: 'listDates' }).then((j) => { setDates(j.rows); setRetentionDays(j.retentionDays || 365); }).catch(() => setDates([]));
  }, []);
  useEffect(() => { loadDates(); }, [loadDates]);
  useEffect(() => {
    fetch('/.netlify/functions/roster-drivers').then((r) => r.json())
      .then((d) => setDrivers(Array.isArray(d) ? d.filter((x) => x && x.login) : [])).catch(() => {});
  }, []);
  useEffect(() => {
    post({ action: 'whoami' })
      .then((j) => { setScope(j); if (!j.isAdmin && !incomingArticle) setTab('case'); })
      .catch(() => setScope({ isAdmin: false, areas: null, drivers: null }));
  }, [incomingArticle]);

  // A scoped leader only ever picks from their own drivers.
  const allowed = scope && scope.drivers ? new Set(scope.drivers) : null;
  const pickable = allowed ? drivers.filter((d) => allowed.has((d.login || '').toUpperCase())) : drivers;
  const visibleTabs = TABS.filter((t) => t.key !== 'upload' || isAdmin);

  const latestDate = dates && dates.length ? dates[0].date : '';

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: '1.4rem' }}>Scanner Audit</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--hp-dim)', fontSize: '0.9rem' }}>
        Full-detail daily scan store — every Accept / Onboard / Delivered / Attempted / Transfer event, with address &amp; signatory.
        Raw rows kept {retentionDays} days.
      </p>
      {scopedTo && (
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
          Showing <strong style={{ color: 'var(--hp-text)' }}>{scopedTo.map((a) => AREA_NAME[a] || a).join(', ')}</strong> drivers only —
          a lookup for anyone else will say they're not on your team.
        </p>
      )}

      {/* loaded-dates strip */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {dates === null ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>Loading loaded dates…</span>
          : dates.length === 0 ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>No days loaded yet — start on the Upload tab.</span>
            : dates.slice(0, 14).map((d) => (
              <span key={d.date} style={{ padding: '4px 9px', borderRadius: 20, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', fontSize: '0.78rem' }}
                title={isAdmin ? `${d.rows.toLocaleString()} rows · ${d.drivers} drivers` : 'This day is loaded'}>
                {d.date}{isAdmin ? ` · ${d.drivers}d` : ''}
              </span>
            ))}
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--hp-border)', marginBottom: 18 }}>
        {visibleTabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem',
            color: tab === t.key ? 'var(--hp-text)' : 'var(--hp-dim)', fontWeight: tab === t.key ? 600 : 400,
            borderBottom: tab === t.key ? '2px solid var(--hp-accent, #2563eb)' : '2px solid transparent', marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'upload' && <UploadTab onDone={loadDates} />}
      {tab === 'case' && <CaseTab initialArticle={caseArticle} initialCaseId={incomingCase} />}
      {tab === 'address' && <AddressTab drivers={pickable} onOpenArticle={(a) => { setCaseArticle(a); setTab('case'); }} />}
      {tab === 'driver' && <DriverDayTab drivers={pickable} defaultDate={latestDate} />}
      {tab === 'downtime' && <DowntimeTab defaultDate={latestDate} />}
      {tab === 'sequence' && <SequenceTab drivers={pickable} defaultDate={latestDate} />}
    </div>
  );
}

// ── Upload ──────────────────────────────────────────────────────────────────
function UploadTab({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [log, setLog] = useState([]);   // [{ id, state:'ok'|'warn'|'error', msg, retry:{name,b64} }]

  // status: 'ok' loaded · 'warn' already-loaded (offers Load anyway) · 'error'
  const describe = (name, j) => {
    if (j.alreadyLoaded && (!j.loaded || j.loaded.length === 0)) {
      return { state: 'warn', msg: `${name}: ${j.message || 'Already loaded — nothing re-imported.'}` };
    }
    const loadedTxt = (j.loaded || j.dates || []).map((x) => `${x.date} (${x.rows.toLocaleString()} rows, ${x.drivers}d)`).join(', ');
    const skippedTxt = (j.skipped || []).length ? ` · skipped (already loaded): ${j.skipped.map((s) => s.date).join(', ')}` : '';
    const prefix = j.forced ? 'Re-imported ' : 'Loaded ';
    // A day's parcels are often reassigned in Parcel Audit before its audit file arrives;
    // ingest replays those corrections, so say which scans changed hands on the way in.
    const fx = (j.loginFixes && j.loginFixes.applied) || [];
    const fixTxt = fx.length
      ? ` · login corrections applied: ${fx.map((f) => `${f.rows.toLocaleString()} scans ${f.from} → ${f.to} on ${f.date}`).join('; ')}`
      : '';
    return { state: 'ok', msg: `${name}: ${prefix}${loadedTxt || j.rows + ' rows'}${skippedTxt}${fixTxt}` };
  };

  // After a gateway timeout the import is still running server-side; the per-day
  // summary row is written LAST, so a new date appearing in listDates means that
  // day's import completed. Poll for it instead of reporting a bogus failure.
  const waitForImport = async (before) => {
    const deadline = Date.now() + 150000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const j = await post({ action: 'listDates' });
        const fresh = (j.rows || []).filter((d) => !before.has(d.date));
        if (fresh.length) return fresh;
      } catch { /* transient — keep polling until the deadline */ }
    }
    return null;
  };

  const ingestOne = async (name, b64, force, replaceId) => {
    setBusy(true);
    // Snapshot the loaded dates first so a gateway-timeout recovery can tell which
    // date(s) this file added. If the snapshot itself fails the poll just degrades.
    let before = new Set();
    try { before = new Set(((await post({ action: 'listDates' })).rows || []).map((d) => d.date)); } catch { /* ignore */ }
    try {
      const j = await post({ action: 'ingest', fileName: name, fileBase64: b64, force });
      const d = describe(name, j);
      const entry = { id: replaceId || `${name}-${b64.length}`, ...d, retry: d.state === 'warn' ? { name, b64 } : null };
      setLog((L) => { const rest = L.filter((x) => x.id !== entry.id); return [...rest, entry]; });
    } catch (e) {
      let entry;
      if (e.gatewayTimeout) {
        setLog((L) => [...L.filter((x) => x.id !== `${name}-wait`), { id: `${name}-wait`, state: 'warn', msg: `${name}: still importing — the server reply timed out but the import continues. Watching for it to finish…`, retry: null }]);
        const fresh = await waitForImport(before);
        entry = fresh
          ? { id: `${name}-wait`, state: 'ok', msg: `${name}: Loaded ${fresh.map((x) => `${x.date} (${x.rows.toLocaleString()} rows, ${x.drivers}d)`).join(', ')} (completed in the background)`, retry: null }
          : { id: `${name}-wait`, state: 'warn', msg: `${name}: import is taking longer than usual. It's still running server-side — check the dates strip in a few minutes before re-uploading.`, retry: null };
      } else {
        entry = { id: replaceId || `${name}-err`, state: 'error', msg: `${name}: ${e.message}`, retry: null };
      }
      setLog((L) => { const rest = L.filter((x) => x.id !== entry.id); return [...rest, entry]; });
    } finally { setBusy(false); onDone(); }
  };

  const upload = async (files) => {
    const list = Array.from(files || []).filter((f) => /\.xlsx$/i.test(f.name));
    if (!list.length) {
      if (files && files.length) setLog([{ id: 'badtype', state: 'error', msg: 'Only .xlsx Scanner Audit files are accepted.', retry: null }]);
      return;
    }
    setLog([]);
    for (const file of list) {
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
        fr.onerror = reject; fr.readAsDataURL(file);
      });
      await ingestOne(file.name, b64, false);
    }
  };

  // Drag-and-drop onto the box. dragOver must preventDefault on every dragover event
  // or the browser navigates to the file instead of firing our drop handler.
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (!busy) upload(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragOver(false); };

  const bg = { ok: 'rgba(34,197,94,0.08)', warn: 'rgba(250,204,21,0.12)', error: 'rgba(239,68,68,0.08)' };
  const mark = { ok: '✓ ', warn: '⚠ ', error: '✕ ' };

  return (
    <div>
      <div onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        style={{ border: `2px dashed ${dragOver ? 'var(--hp-accent, #2563eb)' : 'var(--hp-border)'}`, borderRadius: 12,
          padding: '28px 20px', textAlign: 'center', transition: 'border-color 0.15s, background 0.15s',
          background: dragOver ? 'rgba(37,99,235,0.08)' : 'var(--hp-surface)' }}>
        <p style={{ margin: '0 0 12px', color: 'var(--hp-dim)', pointerEvents: 'none' }}>
          {dragOver ? 'Drop to upload…' : <>Drag the day's Scanner Audit <code>.xlsx</code> here, or choose it below (one file per day). If a day's already loaded it'll say so and skip it — no duplicates.</>}
        </p>
        <label style={{ ...btnPrimary, display: 'inline-block' }}>
          {busy ? 'Uploading…' : 'Choose file(s)'}
          <input type="file" accept=".xlsx" multiple disabled={busy} style={{ display: 'none' }}
            onChange={(e) => upload(e.target.files)} />
        </label>
      </div>
      {log.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, marginTop: 16 }}>
          {log.map((l) => (
            <li key={l.id} style={{ padding: '8px 12px', marginBottom: 6, borderRadius: 8, fontSize: '0.85rem',
              border: '1px solid var(--hp-border)', background: bg[l.state], display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>{mark[l.state]}{l.msg}</span>
              {l.retry && (
                <button style={{ ...btn, padding: '4px 10px', fontSize: '0.8rem', whiteSpace: 'nowrap' }} disabled={busy}
                  onClick={() => ingestOne(l.retry.name, l.retry.b64, true, l.id)}>Load anyway</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Case Lookup ─────────────────────────────────────────────────────────────
function CaseDayView({ day, before, after, multi, picked, onPick }) {
  const t = day.target || {};
  return (
    <div style={{ marginBottom: 22, ...(multi ? { border: '1px solid var(--hp-border)', borderRadius: 10, padding: '12px 14px', opacity: picked ? 1 : 0.6 } : null) }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        {multi && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--hp-dim)', cursor: 'pointer' }}>
            <input type="checkbox" checked={picked} onChange={(e) => onPick(e.target.checked)} /> Push
          </label>
        )}
        <h3 style={{ margin: 0, fontSize: '1rem' }}>{day.date} · {day.driver}</h3>
        <span style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
          {t.event || '—'}{t.scan_time ? ` at ${t.scan_time}` : ''}{day.flag && day.flag.signatory ? ` · signed ${day.flag.signatory}` : ''}
        </span>
      </div>

      {day.flag && day.flag.selfSignature && (
        <div style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 12, border: '1px solid var(--hp-danger, #dc2626)', background: 'rgba(220,38,38,0.08)' }}>
          <strong style={{ color: 'var(--hp-danger, #dc2626)' }}>⚑ Self-signature pattern</strong>
          <div style={{ fontSize: '0.88rem', marginTop: 4 }}>{day.flag.note}</div>
        </div>
      )}

      {!day.neighbours.length ? (
        <div style={{ fontSize: '0.86rem', color: 'var(--hp-dim)' }}>No deliveries by {day.driver} that day to show around it — the article was only {day.events.join(' / ').toLowerCase()}.</div>
      ) : (
        <>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 4 }}>{before} deliveries before · {after} after</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}></th><th style={th}>Time</th><th style={th}>Gap</th><th style={th}>Event</th><th style={th}>Article</th><th style={th}>Address</th><th style={th}>Signatory</th></tr></thead>
              <tbody>
                {day.neighbours.map((r, i) => (
                  <tr key={i} style={r.isTarget ? { background: 'rgba(250,204,21,0.18)' } : null}>
                    <td style={{ ...cell, color: 'var(--hp-dim)', fontWeight: r.isTarget ? 700 : 400 }}>{r.isTarget ? '► THIS' : (r.position > 0 ? `+${r.position}` : r.position)}</td>
                    <td style={cell}>{r.time}</td>
                    <td style={{ ...cell, color: 'var(--hp-dim)' }}>{r.gapMins ? `${r.gapMins}m` : ''}</td>
                    <td style={cell}>{r.isTarget ? <strong>{r.event}</strong> : r.event}</td>
                    <td style={r.isTarget ? { ...artCell, color: 'inherit', fontWeight: 600 } : artCell}>{r.article || '—'}</td>
                    <td style={{ ...cell, whiteSpace: 'normal' }}>{r.address || '—'}</td>
                    <td style={cell}>{r.signatory || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CaseTab({ initialArticle, initialCaseId }) {
  const [article, setArticle] = useState(initialArticle || '');
  const [before, setBefore] = useState(2);
  const [after, setAfter] = useState(3);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState([]);         // per-day "include in the push"
  const [review, setReview] = useState(false);     // push review panel open
  const [notify, setNotify] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);     // { ok, text }

  // `keep` = a re-run for new before/after counts on the same article: keep the result on
  // screen (no flash) and keep which days were ticked.
  const run = async (a = article, b = before, af = after, keep = false) => {
    a = (a || '').trim(); if (!a) return;
    setLoading(true); setError(''); setReview(false); setPushMsg(null);
    if (!keep) setRes(null);
    try {
      const j = await post({ action: 'caseLookup', article: a, before: b, after: af });
      const days = j.days && j.days.length ? j.days : [{ date: j.date, driver: j.driver, target: j.target, neighbours: j.neighbours, flag: j.flag, events: [] }];
      setRes({ ...j, days });
      setPicked((p) => (keep && p.length === days.length ? p : days.map(() => true)));
    } catch (e) { setError(e.message); if (!keep) setRes(null); }
    finally { setLoading(false); }
  };

  // Before/After are live: once a result is on screen, changing a count re-runs it.
  useEffect(() => {
    if (!res) return undefined;
    if (res.before === before && res.after === after) return undefined;
    const t = setTimeout(() => run(res.article, before, after, true), 350);
    return () => clearTimeout(t);
  }, [before, after]); // eslint-disable-line

  const days = res ? res.days : [];
  const multi = days.length > 1;
  const chosen = days.filter((_, i) => picked[i]);

  // Push the chosen days' before/after views to the driver's portal (via the case). Resolves
  // the case by the id we arrived with, else by the article's open case (server-side). The
  // top-level snapshot is the first chosen day, so older portal builds still show that one.
  const pushToDriver = async () => {
    if (!res || !chosen.length) return;
    setPushing(true); setPushMsg(null);
    const dayOut = (d) => ({
      driver: d.driver, date: d.date,
      target: { time: d.target.scan_time, address: d.target.address, event: d.target.event, signatory: d.target.signatory },
      neighbours: d.neighbours.map((n) => ({ position: n.position, isTarget: n.isTarget, time: n.time, event: n.event, address: n.address, signatory: n.signatory, gapMins: n.gapMins })),
    });
    const first = dayOut(chosen[0]);
    const snapshot = {
      article: res.article, ...first, before: res.before, after: res.after,
      ...(chosen.length > 1 ? { days: chosen.map(dayOut) } : {}),
    };
    try {
      const r = await fetch('/.netlify/functions/customer-cases-admin-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'pushScanAudit', id: initialCaseId || undefined, article: res.article, snapshot, notify }),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Push failed');
      const what = chosen.length > 1 ? `${chosen.length} days (${chosen.map((d) => d.date).join(', ')})` : chosen[0].date;
      setPushMsg({ ok: true, text: `Pushed ${what} to ${j.driver}${j.smsSent ? ' and texted them' : ''}. Case ${j.caseNumber || j.caseId}.` });
      setReview(false);
    } catch (e) { setPushMsg({ ok: false, text: e.message }); }
    finally { setPushing(false); }
  };

  // Arriving from the Customer Cases page with ?article=… — run it straight away.
  useEffect(() => { if (initialArticle) { setArticle(initialArticle); run(initialArticle, before, after); } }, [initialArticle]); // eslint-disable-line

  const drivers = [...new Set(days.map((d) => d.driver))];
  const lastDelivered = [...days].reverse().find((d) => d.outcome === 'delivered');
  let prevDate = null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...field, minWidth: 320 }} placeholder="Article / tracking number (e.g. 9LAZ00000708FPP00001)"
          value={article} onChange={(e) => setArticle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()} />
        <label style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>Before
          <input type="number" min={0} max={20} style={{ ...field, width: 60, marginLeft: 6 }} value={before} onChange={(e) => setBefore(Math.min(20, Math.max(0, +e.target.value || 0)))} />
        </label>
        <label style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>After
          <input type="number" min={0} max={20} style={{ ...field, width: 60, marginLeft: 6 }} value={after} onChange={(e) => setAfter(Math.min(20, Math.max(0, +e.target.value || 0)))} />
        </label>
        <button style={btnPrimary} onClick={() => run()} disabled={loading}>{loading ? 'Looking…' : 'Look up'}</button>
        {loading && res && <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Updating…</span>}
      </div>
      {error && <div style={{ color: 'var(--hp-danger, #dc2626)', fontSize: '0.9rem', marginBottom: 12 }}>{error}</div>}
      {res && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <StatCard label={drivers.length > 1 ? 'Drivers' : 'Driver'} value={drivers.join(', ')} />
            <StatCard label={multi ? 'Days scanned' : 'Date'} value={multi ? days.length : res.date} title={days.map((d) => d.date).join(', ')} />
            <StatCard label={multi ? 'Last delivered' : 'Delivered'} value={lastDelivered ? (multi ? `${lastDelivered.date.slice(5)} ${lastDelivered.target.scan_time}` : lastDelivered.target.scan_time) : (res.target.scan_time || '—')} />
            <StatCard label="Signatory" value={(lastDelivered || res).flag.signatory || '—'} />
          </div>

          {multi && (
            <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 16, border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.08)', fontSize: '0.88rem' }}>
              <strong>Scanned on {days.length} days</strong> — {days.map((d) => `${d.date} (${d.driver}, ${d.outcome})`).join(' · ')}.
              {' '}Each run's before/after is below; tick the ones to push to the driver.
              {res.hiddenDays > 0 && <div style={{ marginTop: 4, color: 'var(--hp-dim)' }}>{res.hiddenDays} more day{res.hiddenDays > 1 ? 's' : ''} on another team's driver not shown.</div>}
              {res.truncatedDays > 0 && <div style={{ marginTop: 4, color: 'var(--hp-dim)' }}>Showing the first {days.length}; {res.truncatedDays} later day{res.truncatedDays > 1 ? 's' : ''} not shown.</div>}
              <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                <button style={{ ...btn, padding: '4px 10px', fontSize: '0.8rem' }} onClick={() => setPicked(days.map(() => true))}>Select all</button>
                <button style={{ ...btn, padding: '4px 10px', fontSize: '0.8rem' }} onClick={() => setPicked(days.map(() => false))}>Select none</button>
              </div>
            </div>
          )}

          {days.map((d, i) => (
            <CaseDayView key={`${d.date}|${d.driver}`} day={d} before={res.before} after={res.after} multi={multi}
              picked={!!picked[i]} onPick={(v) => setPicked((p) => days.map((_, k) => (k === i ? v : !!p[k])))} />
          ))}

          <details>
            <summary style={{ cursor: 'pointer', color: 'var(--hp-dim)', fontSize: '0.88rem', marginBottom: 8 }}>Full article timeline ({res.fullTimeline.length} events{multi ? `, ${days.length} days` : ''})</summary>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr><th style={th}>Date</th><th style={th}>Time</th><th style={th}>Event</th><th style={th}>Driver</th><th style={th}>Work Centre</th><th style={th}>Address</th><th style={th}>Signatory</th></tr></thead>
                <tbody>
                  {res.fullTimeline.map((r, i) => {
                    const newDay = prevDate !== null && r.date !== prevDate; prevDate = r.date;
                    const c = newDay ? { ...cell, borderTop: '2px solid var(--hp-border)' } : cell;
                    return (
                      <tr key={i}><td style={c}>{r.date || '—'}</td><td style={c}>{r.time}</td><td style={c}>{r.event}</td><td style={c}>{r.driver || '—'}</td><td style={c}>{r.work_centre}</td><td style={{ ...c, whiteSpace: 'normal' }}>{r.address || '—'}</td><td style={c}>{r.signatory || '—'}</td></tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>

          <div style={{ borderTop: '1px solid var(--hp-border)', marginTop: 20, paddingTop: 16 }}>
            {!review ? (
              <>
                <button style={btnPrimary} disabled={!chosen.length} onClick={() => { setReview(true); setPushMsg(null); }}>
                  {multi ? `Review & push ${chosen.length} of ${days.length} days to driver →` : 'Review & push to driver →'}
                </button>
                {multi && !chosen.length && <span style={{ marginLeft: 12, fontSize: '0.85rem', color: 'var(--hp-dim)' }}>Tick at least one day to push.</span>}
                {pushMsg && (
                  <span style={{ marginLeft: 12, fontSize: '0.88rem', color: pushMsg.ok ? 'var(--hp-green, #16a34a)' : 'var(--hp-danger, #dc2626)' }}>
                    {pushMsg.ok ? '✓ ' : '✕ '}{pushMsg.text}
                  </span>
                )}
              </>
            ) : (
              <div style={{ border: '1px solid var(--hp-border)', borderRadius: 10, padding: 16, background: 'var(--hp-surface)' }}>
                <strong style={{ fontSize: '0.95rem' }}>Push to the driver on this case</strong>
                <p style={{ fontSize: '0.86rem', color: 'var(--hp-dim)', margin: '6px 0 12px' }}>
                  The driver assigned to {initialCaseId ? 'this case' : `the open case for article ${res.article}`} will see the plain-language
                  before/after view ({res.before} before · {res.after} after) for{' '}
                  <strong style={{ color: 'var(--hp-text)' }}>{chosen.map((d) => `${d.date} (${d.driver})`).join(', ')}</strong> on their portal
                  and can reply in the case thread. It goes to the case’s own driver, replaces anything pushed before, and stays visible while
                  the case is open. The confirmation shows who it reached.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={btnPrimary} onClick={pushToDriver} disabled={pushing || !chosen.length}>{pushing ? 'Pushing…' : 'Confirm push'}</button>
                  <button style={btn} onClick={() => setReview(false)} disabled={pushing}>Cancel</button>
                  <label style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Text the driver
                  </label>
                </div>
                {pushMsg && !pushMsg.ok && <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--hp-danger, #dc2626)' }}>{pushMsg.text}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Address Search ──────────────────────────────────────────────────────────
function AddressTab({ drivers, onOpenArticle }) {
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [driver, setDriver] = useState('');
  const [allEvents, setAllEvents] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');

  const run = async (all = allEvents) => {
    if (!query.trim()) return;
    setLoading(true); setError(''); setRes(null);
    try { setRes(await post({ action: 'addressSearch', query, from, to, driver, allEvents: all })); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...field, minWidth: 300 }} placeholder="Address (e.g. 528 Kent St)"
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()} />
        <DriverPicker drivers={drivers} value={driver} onChange={setDriver} />
        <label style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>From
          <input type="date" style={{ ...field, marginLeft: 6 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>To
          <input type="date" style={{ ...field, marginLeft: 6 }} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button style={btnPrimary} onClick={() => run()} disabled={loading}>{loading ? 'Searching…' : 'Search'}</button>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.83rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
        <input type="checkbox" checked={allEvents} onChange={(e) => { setAllEvents(e.target.checked); if (res) run(e.target.checked); }} />
        All event types — otherwise just delivery outcomes (Delivered / Attempted). "Kent Street" also finds "KENT ST"; leave the dates blank to search every loaded day.
      </label>
      {error && <div style={{ color: 'var(--hp-danger, #dc2626)', fontSize: '0.9rem', marginBottom: 12 }}>{error}</div>}
      {res && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <StatCard label="Scans" value={res.total} />
            <StatCard label="Articles" value={res.articles} />
            <StatCard label="Drivers" value={res.drivers} />
            <StatCard label="Days" value={res.days} />
          </div>
          {(res.truncated || res.hitScanCap) && (
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', margin: '0 0 10px' }}>
              {res.truncated ? `Showing the newest ${res.rows.length} of ${res.total} scans — ` : 'Very common address term — '}
              narrow with a date range, a driver, or a more specific address.
            </p>
          )}
          {res.total === 0 ? (
            <div style={{ color: 'var(--hp-dim)' }}>No scans match “{res.query}”.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr><th style={th}>Date</th><th style={th}>Time</th><th style={th}>Driver</th><th style={th}>Event</th><th style={th}>Article</th><th style={th}>Address</th><th style={th}>Signatory</th><th style={th}></th></tr></thead>
                <tbody>
                  {res.rows.map((r, i) => (
                    <tr key={i}>
                      <td style={cell}>{r.date}</td>
                      <td style={cell}>{r.time}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{r.driver}</td>
                      <td style={cell}>{r.event}</td>
                      <td style={artCell}>{r.article || '—'}</td>
                      <td style={{ ...cell, whiteSpace: 'normal' }}>{r.address || '—'}</td>
                      <td style={cell}>{r.signatory || '—'}</td>
                      <td style={cell}>
                        <button style={{ ...btn, padding: '3px 9px', fontSize: '0.78rem' }} title={`Case Lookup ${r.article}`}
                          onClick={() => onOpenArticle(r.article)}>Case →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Driver Day ──────────────────────────────────────────────────────────────
function DriverPicker({ drivers, value, onChange }) {
  return (
    <>
      <input style={{ ...field, minWidth: 200 }} list="sa-drivers" placeholder="Driver login" value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())} />
      <datalist id="sa-drivers">
        {drivers.map((d) => <option key={d.login} value={d.login}>{d.name || ''}</option>)}
      </datalist>
    </>
  );
}

function DriverDayTab({ drivers, defaultDate }) {
  const [driver, setDriver] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { if (defaultDate && !date) setDate(defaultDate); }, [defaultDate]); // eslint-disable-line

  const run = async () => {
    if (!driver || !date) return;
    setLoading(true); setError(''); setRes(null);
    try { setRes(await post({ action: 'driverDay', driver, date })); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <DriverPicker drivers={drivers} value={driver} onChange={setDriver} />
        <input type="date" style={field} value={date} onChange={(e) => setDate(e.target.value)} />
        <button style={btnPrimary} onClick={run} disabled={loading}>{loading ? 'Loading…' : 'Load day'}</button>
      </div>
      {error && <div style={{ color: 'var(--hp-danger, #dc2626)', fontSize: '0.9rem', marginBottom: 12 }}>{error}</div>}
      {res && res.summary && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatCard label="First scan" value={res.summary.firstScan} />
            <StatCard label="Last scan" value={res.summary.lastScan} />
            <StatCard label="Span" value={`${Math.floor(res.summary.spanMins / 60)}h ${res.summary.spanMins % 60}m`} />
            <StatCard label="Onboarded" value={res.summary.onboarded} />
            <StatCard label="Delivered" value={res.summary.delivered} title={res.summary.onboarded ? `${res.summary.onboarded - res.summary.delivered} not delivered` : undefined} />
            <StatCard label={`Idle (gaps ≥${res.summary.gapMins}m)`} value={`${res.summary.idleMins}m`} title={`${res.summary.gapCount} gaps`} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}>Time</th><th style={th}>Gap</th><th style={th}>Event</th><th style={th}>Article</th><th style={th}>Address</th><th style={th}>Signatory</th></tr></thead>
              <tbody>
                {res.rows.map((r, i) => (
                  <tr key={i} style={r.isGap ? { background: 'rgba(250,204,21,0.14)' } : null}>
                    <td style={cell}>{r.time}</td>
                    <td style={{ ...cell, color: r.isGap ? 'var(--hp-danger, #dc2626)' : 'var(--hp-dim)', fontWeight: r.isGap ? 600 : 400 }}>{r.gapMins ? `${r.gapMins}m` : ''}</td>
                    <td style={cell}>{r.event}</td>
                    <td style={artCell}>{r.article || '—'}</td>
                    <td style={{ ...cell, whiteSpace: 'normal' }}>{r.address || '—'}</td>
                    <td style={cell}>{r.signatory || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {res && !res.summary && <div style={{ color: 'var(--hp-dim)' }}>No scans for {driver} on {date}.</div>}
    </div>
  );
}

// ── Downtime ────────────────────────────────────────────────────────────────
function DowntimeTab({ defaultDate }) {
  const [date, setDate] = useState(defaultDate);
  const [gapMins, setGapMins] = useState(20);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { if (defaultDate && !date) setDate(defaultDate); }, [defaultDate]); // eslint-disable-line

  const run = async () => {
    if (!date) return;
    setLoading(true); setError(''); setRes(null);
    try { setRes(await post({ action: 'downtime', date, gapMins })); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" style={field} value={date} onChange={(e) => setDate(e.target.value)} />
        <label style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>Gap ≥
          <input type="number" min={5} style={{ ...field, width: 70, marginLeft: 6 }} value={gapMins} onChange={(e) => setGapMins(+e.target.value || 20)} /> min
        </label>
        <button style={btnPrimary} onClick={run} disabled={loading}>{loading ? 'Loading…' : 'Show downtime'}</button>
      </div>
      {error && <div style={{ color: 'var(--hp-danger, #dc2626)', fontSize: '0.9rem', marginBottom: 12 }}>{error}</div>}
      {res && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={th}>Driver</th><th style={th}>First</th><th style={th}>Last</th><th style={th}>Delivered</th><th style={th}>Biggest gap</th><th style={th}>When</th><th style={th}>Total idle</th></tr></thead>
            <tbody>
              {res.rows.map((r) => (
                <tr key={r.driver} style={r.biggestGapMins >= 60 ? { background: 'rgba(239,68,68,0.08)' } : null}>
                  <td style={{ ...cell, fontWeight: 600 }}>{r.driver}</td>
                  <td style={cell}>{r.firstScan}</td>
                  <td style={cell}>{r.lastScan}</td>
                  <td style={cell}>{r.delivered}</td>
                  <td style={{ ...cell, fontWeight: 600, color: r.biggestGapMins >= 60 ? 'var(--hp-danger, #dc2626)' : 'inherit' }}>{r.biggestGapMins}m</td>
                  <td style={cell}>{r.biggestGapAt}</td>
                  <td style={cell}>{r.idleMins}m <span style={{ color: 'var(--hp-dim)' }}>({r.gapCount})</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Route Sequence ──────────────────────────────────────────────────────────
function SequenceTab({ drivers, defaultDate }) {
  const [driver, setDriver] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { if (defaultDate && !date) setDate(defaultDate); }, [defaultDate]); // eslint-disable-line

  const run = async () => {
    if (!driver || !date) return;
    setLoading(true); setError(''); setRes(null);
    try { setRes(await post({ action: 'sequence', driver, date })); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const exportXlsx = () => {
    if (!res || !res.stops.length) return;
    const aoa = [['Seq', 'Time', 'Gap (min)', 'Article', 'Address', 'Suburb', 'Event'],
      ...res.stops.map((s) => [s.seq, s.time, s.gapMins, s.article || '', s.address, s.suburb, s.event])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Run order');
    XLSX.writeFile(wb, `run-order-${driver}-${date}.xlsx`);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <DriverPicker drivers={drivers} value={driver} onChange={setDriver} />
        <input type="date" style={field} value={date} onChange={(e) => setDate(e.target.value)} />
        <button style={btnPrimary} onClick={run} disabled={loading}>{loading ? 'Loading…' : 'Load run order'}</button>
        {res && res.stops.length > 0 && <button style={btn} onClick={exportXlsx}>⤓ Excel</button>}
      </div>
      {error && <div style={{ color: 'var(--hp-danger, #dc2626)', fontSize: '0.9rem', marginBottom: 12 }}>{error}</div>}
      {res && (
        <>
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.88rem', margin: '0 0 12px' }}>{res.count} deliveries in scan order — the actual run on {res.date}.</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}>#</th><th style={th}>Time</th><th style={th}>Gap</th><th style={th}>Article</th><th style={th}>Address</th><th style={th}>Suburb</th></tr></thead>
              <tbody>
                {res.stops.map((s) => (
                  <tr key={s.seq} style={s.gapMins >= 20 ? { background: 'rgba(250,204,21,0.12)' } : null}>
                    <td style={{ ...cell, color: 'var(--hp-dim)' }}>{s.seq}</td>
                    <td style={cell}>{s.time}</td>
                    <td style={{ ...cell, color: s.gapMins >= 20 ? 'var(--hp-danger, #dc2626)' : 'var(--hp-dim)' }}>{s.gapMins ? `${s.gapMins}m` : ''}</td>
                    <td style={artCell}>{s.article || '—'}</td>
                    <td style={{ ...cell, whiteSpace: 'normal' }}>{s.address || '—'}</td>
                    <td style={cell}>{s.suburb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
